import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Copy, ChevronDown, ChevronUp, ExternalLink, AlertTriangle, ArrowUpRight, ArrowDownRight, Minus,
  Users, UserPlus, CalendarCheck, FileSignature, UserMinus, ClipboardList, CalendarRange, Sparkles, PhoneCall, TrendingUp, ChevronRight, Pencil, Check, X, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { aufgabenAPI, kpiAPI, onboardingAPI, authAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import StarterpaketKarte from '@/components/StarterpaketKarte';
import Zeiterfassung from '@/components/Zeiterfassung';
import UebergabeFeld from '@/components/Uebergabe';
import HeuteCockpit from '@/components/HeuteCockpit';
import PiaPanel from '@/components/PiaPanel';
import StartUebersicht, { Klappe, wann, kuerzel } from '@/components/StartUebersicht';
import api from '@/lib/api';

/* Startseite /heute (Erweiterung v6, M8): Cagla arbeitet morgens EINE Seite ab.
 *
 *   Block 1  sechs KPI-Kacheln (Delta zur Vorwoche aus dem Nacht-Snapshot, Ziel)
 *   Block 2  Aufgaben heute + morgen, gruppiert Onboarding · Nachfass ·
 *            Reaktivierung · Verträge · Freigabe — Überfällige oben, rot
 *   Block 3  Kurse heute mit Auslastung (voll / 2. Einheit verkaufbar)
 *   Block 4  Neumitglieder der letzten 30 Tage mit fünf Ampelpunkten
 *
 * Optik: das normale CRM-Design (Theme-Tokens, hell und dunkel) wie Dashboard
 * und Freigabe — die Funnel-Farben aus dem Brief passten nicht ins CRM. */

function euro(v) { return (v ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €'; }
function zahl(v) { return (v ?? 0).toLocaleString('de-DE'); }
function tagUhr(iso, heute) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const morgen = new Date(heute); morgen.setDate(morgen.getDate() + 1);
  const t = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === heute.toDateString()) return `heute ${t}`;
  if (d.toDateString() === morgen.toDateString()) return `morgen ${t}`;
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function gruss(d) { const h = d.getHours(); return h < 11 ? 'Guten Morgen' : h < 17 ? 'Hallo' : 'Guten Abend'; }

/* Kacheln wie auf dem Dashboard: Icon im farbigen Quadrat, P1-Rot als
   Leitfarbe, je Kennzahl eine eigene Farbe. */
const AKZENT = 'hsl(var(--marke))';
const P1_ROT = AKZENT;        // im CRM die gewählte Akzentfarbe, sonst P1 Rot (Neugestaltung 12.10.2026)
const KACHEL_STIL = {
  leads_woche: { icon: UserPlus, color: '#3B82F6' },
  probetrainings: { icon: CalendarCheck, color: '#F59E0B' },
  abschluesse: { icon: FileSignature, color: '#8B5CF6' },
  abschlussquote: { icon: Target, color: '#10B981' },
  follow_ups: { icon: PhoneCall, color: '#F97316' },
  kuendigungen_30: { icon: UserMinus, color: '#EF4444' },
  mitglieder: { icon: Users, color: P1_ROT },
  upsells: { icon: TrendingUp, color: '#10B981' },
};

/* Kacheln klickbar (Betreiber 19.09.2026): jede Kennzahl fuehrt in den
   Bereich, in dem man sie beeinflusst. Follow-up springt zur Aufgabenliste. */
const KACHEL_ZIEL = {
  leads_woche: { to: '/leads?stage=neuer_lead', titel: 'Neue Leads öffnen' },
  probetrainings: { to: '/probetrainings', titel: 'Probetrainings öffnen' },
  abschluesse: { to: '/vertraege', titel: 'Verträge öffnen' },
  abschlussquote: { to: '/kennzahlen/vertrieb', titel: 'Sales Tracking öffnen' },
  follow_ups: { to: '#aufgaben', titel: 'Zu den Aufgaben' },
  kuendigungen_30: { to: '/mitgliedschaften', titel: 'Mitglieder öffnen' },
  mitglieder: { to: '/mitgliedschaften', titel: 'Mitglieder öffnen' },
  upsells: { to: '/kursbestand', titel: 'Kursbestand (Upsells) öffnen' },
};
function Kachel({ k, navigate }) {
  const ziel = KACHEL_ZIEL[k.key];
  const oeffnen = () => {
    if (!ziel) return;
    if (ziel.to.startsWith('#')) { document.getElementById(ziel.to.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    navigate(ziel.to);
  };
  const wert = k.einheit === '€' ? euro(k.wert) : k.einheit === '%' ? `${zahl(k.wert)} %` : zahl(k.wert);
  const delta = k.delta;
  const gut = delta == null ? null : (k.invers ? delta <= 0 : delta >= 0);
  const anteil = k.ziel ? Math.min(100, Math.round((k.wert / k.ziel) * 100)) : null;
  const DeltaIcon = delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
  // Ampel (Sales Tracking): gruen >= 70 %, gelb 50-69 %, rot < 50 % — faerbt die Kachel
  const AMPEL_FARBE = { gruen: '#10B981', gelb: '#F59E0B', rot: '#EF4444' };
  const stil = k.ampel && AMPEL_FARBE[k.ampel]
    ? { ...(KACHEL_STIL[k.key] || { icon: Sparkles }), color: AMPEL_FARBE[k.ampel] }
    : (KACHEL_STIL[k.key] || { icon: Sparkles, color: P1_ROT });
  const Icon = stil.icon;
  return (
    <Card className={`border-border/60 hover:border-primary/30 hover:shadow-md transition-all ${ziel ? 'cursor-pointer' : ''}`} data-testid={`kpi-${k.key}`}
      role={ziel ? 'link' : undefined} tabIndex={ziel ? 0 : undefined} title={ziel?.titel} onClick={oeffnen}
      onKeyDown={(e) => { if (ziel && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); oeffnen(); } }}>
      <CardContent className="p-3 sm:p-4">
        {/* Label und Icon in einer eigenen Zeile, Wert und Zusatz darunter ueber die volle Breite —
            lange Woerter (ABSCHLUSSQUOTE) brechen um statt unter das Icon zu laufen */}
        <div className="flex items-start justify-between gap-2">
          <p lang="de" className="min-w-0 text-[10px] sm:text-[11px] font-medium text-muted-foreground uppercase tracking-wide leading-snug hyphens-auto [overflow-wrap:anywhere]">{k.label}</p>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `color-mix(in srgb, ${stil.color} 12%, transparent)` }}>
            <Icon className="w-4 h-4" style={{ color: stil.color }} />
          </div>
        </div>
        <p className="mt-1 text-xl sm:text-2xl font-bold tracking-tight tabular-nums">{wert}</p>
        <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
          {k.ampel ? <span style={{ color: stil.color }} className="font-medium">{{ gruen: 'Ziel erreicht', gelb: 'unter Ziel', rot: 'deutlich unter Ziel' }[k.ampel]}</span>
            : delta == null ? <span>{k.key === 'abschlussquote' ? 'ab Stichtag 24.09.' : 'noch kein Vorwochenwert'}</span>
            : <span className={`inline-flex items-center gap-0.5 ${gut ? 'text-emerald-600' : 'text-red-500'}`}><DeltaIcon className="w-3 h-3 flex-shrink-0" />{k.einheit === '€' ? euro(Math.abs(delta)) : zahl(Math.abs(delta))} zur Vorwoche</span>}
          {k.zusatz && <span className="block mt-0.5">{k.zusatz}</span>}
        </p>
        {anteil != null && (
          <div className="pt-2" title={`Ziel ${k.einheit === '€' ? euro(k.ziel) : zahl(k.ziel)}`}>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full" style={{ width: `${anteil}%`, backgroundColor: stil.color }} /></div>
            <p className="text-[10px] text-muted-foreground mt-1">{anteil} % von {k.einheit === '€' ? euro(k.ziel) : zahl(k.ziel)}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* Delegation (Betreiber 19.09.2026): Inhaber/Manager weisen einzelne Zeilen
   oder ganze Gruppen einem Nutzer zu; ohne Zuweisung gilt die Bereichsregel
   (Cagla: Leads, Probetrainings, No-Shows, Abschluss, Kündigungen; Rest und
   Altlast: Inhaber). */
function NutzerWahl({ nutzer, wert, onChange, leer = '– Nutzer –', klein = true }) {
  return (
    <select value={wert || ''} onChange={(e) => onChange(e.target.value || null)} onClick={(e) => e.stopPropagation()}
      className={`rounded-md border border-border bg-background ${klein ? 'h-6 text-[11px] px-1' : 'h-8 text-xs px-2'} max-w-[160px]`} aria-label="Delegieren an">
      <option value="">{leer}</option>
      {(nutzer || []).map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
    </select>
  );
}

function AufgabeZeile({ a, heute, onErledigt, onVerwerfen, onDelegieren, navigate, leitung, nutzer, zeigeOwner }) {
  const [offen, setOffen] = useState(false);
  const copilot = a.kontext?.copilot;
  const name = a.bezug?.name;
  const kopieren = async () => { try { await navigator.clipboard.writeText(copilot); toast.success('Text kopiert'); } catch { toast.error('Kopieren nicht möglich'); } };
  const Link = ({ to, children }) => <button className="inline-flex items-center gap-0.5 font-medium hover:underline" style={{ color: AKZENT }} onClick={() => navigate(to)}>{children} <ExternalLink className="w-3 h-3" /></button>;
  const ziel = a.kontext?.link || a.link;
  const istFreigabe = (a.id || '').startsWith('fg_');
  // 30.10.2026: Der Haken bei einem Entwurf heißt „schon selbst gesendet“ — das CRM schickt dann nichts. Darum erst nachfragen.
  const [selbstFrage, setSelbstFrage] = useState(false);
  // Posten: die Liste IN der Aufgabe (12.10.2026) — jeder Eintrag anklickbar und einzeln abhakbar; der Stand liegt am
  // Server, die Liste bleibt beim Zurückkommen von einem Lead offen.
  const [posten, setPosten] = useState(a.posten || []);
  const merk = `heute.posten.${a.id}`;
  const [listeOffen, setListeOffen] = useState(() => { try { return sessionStorage.getItem(merk) === '1'; } catch { return false; } });
  const listeSchalten = () => setListeOffen(o => { try { sessionStorage.setItem(merk, o ? '0' : '1'); } catch { /* egal */ } return !o; });
  const fertig = posten.filter(p => p.erledigt_am).length;
  const haken = async (p) => {
    const neu = !p.erledigt_am;
    setPosten(liste => liste.map(x => x.nr === p.nr ? { ...x, erledigt_am: neu ? new Date().toISOString() : null } : x));
    try {
      const r = (await aufgabenAPI.postenHaken(a.id, p.nr, neu)).data;
      if (r.status === 'erledigt') { toast.success('Alle Posten erledigt. Die Aufgabe ist abgeschlossen.'); onErledigt(a); }
    } catch (e) { setPosten(a.posten || []); toast.error(e?.response?.data?.detail || 'Nicht gespeichert'); }
  };
  const oeffnen = (p) => { try { sessionStorage.setItem(merk, '1'); } catch { /* egal */ } navigate(p.link); };
  return (
    <li className={`flex gap-3 py-2.5 border-t border-border/50 first:border-t-0 ${a.ueberfaellig && !a.altlast ? 'bg-red-500/5 -mx-4 px-4' : ''}`} data-testid={`aufgabe-${a.id}`}>
      <input type="checkbox" className="mt-1 w-4 h-4 accent-marke cursor-pointer" aria-label={istFreigabe ? 'Schon selbst gesendet' : 'Erledigt'}
        checked={istFreigabe ? selbstFrage : undefined}
        title={istFreigabe ? 'Schon selbst gesendet (das CRM schickt dann nichts)' : 'Erledigt'} onChange={() => (istFreigabe ? setSelbstFrage(f => !f) : onErledigt(a))} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
          {a.prio === 'hoch' && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">hoch</Badge>}
          <span>{a.titel}</span>
          {a.bereich_label && <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal text-muted-foreground">{a.bereich_label}</Badge>}
          {(zeigeOwner || a.delegiert) && a.owner_name && <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">{a.delegiert ? 'delegiert an ' : ''}{a.owner_name.split(' ')[0]}</Badge>}
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap mt-0.5">
          <span className={a.ueberfaellig && !a.altlast ? 'text-red-600 font-semibold' : ''}>{a.ueberfaellig && !a.altlast ? 'überfällig · ' : ''}{tagUhr(a.faellig_am, heute)}</span>
          {name && a.lead_id && <Link to={`/leads/${a.lead_id}`}>{name}</Link>}
          {a.quelle === 'posteingang' && ziel && <Link to={ziel}>Chat öffnen</Link>}
          {a.quelle !== 'posteingang' && ziel && !a.lead_id && <Link to={ziel}>öffnen</Link>}
          {istFreigabe && <Link to={`/freigabe?id=${a.id}`}>Senden (Freigabe)</Link>}
          {a.quelle === 'reaktivierung' && <Link to="/reaktivierung">Rückrufliste</Link>}
          {copilot && <button className="inline-flex items-center gap-0.5 font-medium hover:underline" style={{ color: AKZENT }} onClick={() => setOffen(o => !o)}>{offen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Entwurf</button>}
          {leitung && <NutzerWahl nutzer={nutzer} wert={a.delegiert ? a.owner : ''} onChange={(uid) => onDelegieren([a.id], uid)} leer="delegieren an …" />}
          <button className="inline-flex items-center gap-0.5 hover:underline" title="Verwerfen" onClick={() => onVerwerfen(a)}><X className="w-3 h-3" /> verwerfen</button>
        </div>
        {selbstFrage && istFreigabe && (
          <div className="mt-2 rounded-md border border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs space-y-2" data-testid={`selbst-frage-${a.id}`}>
            <div className="font-semibold text-sm">Schon selbst gesendet?</div>
            <div className="text-muted-foreground">Das CRM schickt dabei nichts, es trägt die Nachricht nur als gesendet ein. Hast du sie nicht selbst geschickt, bekommt der Kunde nichts. Zum Senden über das CRM: „Senden (Freigabe)“.</div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="h-7 text-xs" onClick={() => { setSelbstFrage(false); onErledigt(a); }}>Ja, schon selbst gesendet</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(`/freigabe?id=${a.id}`)}>Nein, zur Freigabe</Button>
            </div>
          </div>
        )}
        {offen && copilot && (
          <div className="mt-2 rounded-md border border-dashed border-border bg-muted/30 p-3">
            <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed">{copilot}</pre>
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1 mt-2" onClick={kopieren}><Copy className="w-3 h-3" /> Kopieren</Button>
            {a.beschreibung && <p className="text-xs text-muted-foreground mt-2">{a.beschreibung}</p>}
          </div>
        )}
        {!offen && a.beschreibung && !copilot && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{a.beschreibung}</p>}
        {posten.length > 0 && (
          <div className="mt-2" data-testid={`posten-${a.id}`}>
            <button type="button" onClick={listeSchalten} aria-expanded={listeOffen}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold transition-colors hover:bg-accent">
              {listeOffen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              Liste {listeOffen ? 'schließen' : 'öffnen'} · <span className="tabular-nums">{fertig} von {posten.length} erledigt</span>
            </button>
            {listeOffen && (
              <ol className="mt-2 divide-y divide-border/50 rounded-xl border border-border bg-card">
                {posten.map(p => (
                  <li key={p.nr} className="flex items-center gap-3 px-3 py-2">
                    <input type="checkbox" className="w-4 h-4 accent-marke cursor-pointer flex-none" checked={!!p.erledigt_am} onChange={() => haken(p)} aria-label={`${p.text} erledigt`} />
                    <div className="min-w-0 flex-1">
                      {p.link
                        ? <button type="button" onClick={() => oeffnen(p)} className={`text-left text-sm font-medium hover:underline ${p.erledigt_am ? 'text-muted-foreground line-through' : ''}`} style={p.erledigt_am ? undefined : { color: AKZENT }}>{p.text}</button>
                        : <span className={`text-sm font-medium ${p.erledigt_am ? 'text-muted-foreground line-through' : ''}`}>{p.text}</span>}
                      {p.detail && <div className="text-xs text-muted-foreground truncate">{p.detail}</div>}
                    </div>
                    {p.link && !p.erledigt_am && <button type="button" onClick={() => oeffnen(p)} className="flex-none rounded-full border border-border px-2.5 py-0.5 text-[11px] font-semibold hover:bg-accent">öffnen</button>}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/* Aufgaben-Gruppen aufklappbar (Betreiber 18.09.2026): Kopfzeile klickt
   auf/zu, zugeklappt bleibt nur Zahl + Ueberfaellig. Zustand je Gruppe im
   Browser gemerkt; Standard: zu, ausser die Gruppe ist klein. Altlast
   (aelter als 30 Tage) ist immer zu und hat „alle delegieren / verwerfen". */
function gruppeOffenLesen(quelle, anzahl) {
  try { const v = localStorage.getItem(`heute.gruppe.${quelle}`); if (v != null) return v === '1'; } catch { /* egal */ }
  return quelle !== 'altlast';   // Launch 23.09.2026: Aufgaben stehen offen da, Altlast bleibt zu
}
function AufgabenGruppe({ g, heute, onErledigt, onVerwerfen, onDelegieren, onVerwerfenAlle, navigate, leitung, nutzer, zeigeOwner, immerOffen }) {
  const [offen, setOffen] = useState(() => immerOffen || gruppeOffenLesen(g.quelle, g.anzahl));
  const [alle, setAlle] = useState(!!immerOffen);
  const toggle = () => { setOffen(o => { try { localStorage.setItem(`heute.gruppe.${g.quelle}`, o ? '0' : '1'); } catch { /* egal */ } return !o; }); };
  const eintraege = alle ? g.eintraege : g.eintraege.slice(0, 20);
  const ids = g.eintraege.map(e => e.id);
  return (
    <Card className={`border-border/60 ${g.quelle === 'altlast' ? 'opacity-90' : ''}`} data-testid={`gruppe-${g.quelle}`}>
      <CardHeader className={`py-3 px-4 ${offen ? 'border-b border-border/50' : ''}`}>
        <CardTitle className="text-sm font-semibold flex items-center gap-2 flex-wrap">
          <button type="button" onClick={toggle} className="flex items-center gap-2 text-left" aria-expanded={offen}>
            {offen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            {g.label}
            <Badge variant="secondary" className="text-[10px]">{g.anzahl}</Badge>
            {g.ueberfaellig > 0 && <Badge variant="destructive" className="text-[10px]">{g.ueberfaellig} überfällig</Badge>}
          </button>
          {leitung && (
            <span className="ml-auto flex items-center gap-1.5 text-xs font-normal">
              <span className="text-muted-foreground">alle</span>
              <NutzerWahl nutzer={nutzer} wert="" onChange={(uid) => uid && onDelegieren(ids, uid)} leer="delegieren an …" />
              <button className="text-muted-foreground hover:text-red-600 hover:underline" onClick={() => onVerwerfenAlle(g)}>verwerfen</button>
            </span>
          )}
          {!leitung && !offen && <span className="ml-auto text-xs font-normal text-muted-foreground">aufklappen</span>}
        </CardTitle>
      </CardHeader>
      {offen && (
        <CardContent className="px-4 py-1">
          <ul>{eintraege.map(a => <AufgabeZeile key={a.id} a={a} heute={heute} onErledigt={onErledigt} onVerwerfen={onVerwerfen} onDelegieren={onDelegieren} navigate={navigate} leitung={leitung} nutzer={nutzer} zeigeOwner={zeigeOwner} />)}</ul>
          {!alle && g.eintraege.length > 20 && (
            <button className="text-xs py-2 hover:underline" style={{ color: AKZENT }} onClick={() => setAlle(true)}>Alle {g.eintraege.length} anzeigen</button>
          )}
        </CardContent>
      )}
    </Card>
  );
}

/* Zustaendigkeiten je Bereich (nur Leitung): wer bekommt neue Aufgaben ohne
   ausdrueckliche Zuweisung. Standard: Cagla fuer Vertrieb, Inhaber fuer Rest. */
function Zustaendigkeiten({ nutzer }) {
  const [daten, setDaten] = useState(null);
  const [offen, setOffen] = useState(false);
  useEffect(() => { aufgabenAPI.zustaendigkeiten().then(r => setDaten(r.data)).catch(() => setDaten(null)); }, []);
  const setzen = async (bereich, uid) => {
    try { const r = await aufgabenAPI.zustaendigkeitSetzen(bereich, uid); setDaten(d => ({ ...d, bereiche: r.data.bereiche })); toast.success('Zuständigkeit gespeichert'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };
  if (!daten) return null;
  return (
    <Card className="border-border/60" data-testid="zustaendigkeiten">
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm font-semibold">
          <button type="button" onClick={() => setOffen(o => !o)} className="flex items-center gap-2" aria-expanded={offen}>
            {offen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            Zuständigkeiten (Standard je Bereich)
          </button>
        </CardTitle>
      </CardHeader>
      {offen && (
        <CardContent className="px-4 pb-4 pt-0">
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {daten.bereiche.map(b => (
              <li key={b.bereich} className="flex items-center justify-between gap-2 text-xs">
                <span>{b.label}</span>
                <NutzerWahl nutzer={nutzer} wert={b.user?.user_id} onChange={(uid) => setzen(b.bereich, uid)} leer="– Standard –" klein={false} />
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-muted-foreground mt-3">Ohne Zuweisung: Leads, Probetrainings, No-Shows, Abschluss, Kündigungen, Onboarding und Posteingang beim Vertrieb; Reaktivierung, Sonstiges und Altlast (älter als {daten.altlast_tage} Tage) beim Inhaber, der von Hand delegiert.</p>
        </CardContent>
      )}
    </Card>
  );
}

/* Kurse heute direkt bearbeitbar (Betreiber 18.09.2026): Trainer und
   Maximum per Stift in der Zeile aendern — schreibt in den Kursplan
   (PUT /api/kursplan/admin/courses), nur Admin/Manager. */
function KursZeile({ k, darfBearbeiten, onGespeichert, zeigeTrainer }) {
  const [bearbeitet, setBearbeitet] = useState(false);
  const [trainer, setTrainer] = useState(k.trainer || '');
  const [max, setMax] = useState(k.max);
  const [speichert, setSpeichert] = useState(false);
  const speichern = async () => {
    setSpeichert(true);
    try {
      const daten = {};
      if (trainer !== (k.trainer || '')) daten.trainer = trainer.trim();
      if (Number(max) !== k.max) daten.max_teilnehmer = Math.max(1, Number(max) || k.max);
      if (Object.keys(daten).length) { await kpiAPI.kursAendern(k.id, daten); toast.success('Kurs gespeichert'); onGespeichert?.(); }
      setBearbeitet(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSpeichert(false); }
  };
  const abbrechen = () => { setTrainer(k.trainer || ''); setMax(k.max); setBearbeitet(false); };
  return (
    <tr className={`border-b border-border/40 last:border-b-0 ${k.vorbei ? 'text-muted-foreground' : ''}`} data-testid={`kurs-${k.id}`}>
      <td className="px-4 py-2 tabular-nums whitespace-nowrap">{k.start}</td>
      <td className="px-2 py-2">
        <span className="font-medium">{k.name}</span>{k.note && <span className="text-muted-foreground text-xs"> · {k.note}</span>}
        {bearbeitet && !zeigeTrainer && <Input value={trainer} onChange={e => setTrainer(e.target.value)} placeholder="Trainer" className="h-7 text-xs w-32 mt-1" autoFocus onKeyDown={e => { if (e.key === 'Enter') speichern(); if (e.key === 'Escape') abbrechen(); }} />}
      </td>
      {zeigeTrainer && (
        <td className="px-2 py-2">
          {bearbeitet ? <Input value={trainer} onChange={e => setTrainer(e.target.value)} placeholder="Trainer" className="h-7 text-xs w-28" autoFocus onKeyDown={e => { if (e.key === 'Enter') speichern(); if (e.key === 'Escape') abbrechen(); }} />
            : (k.vertretung
              ? <span title={k.trainer ? `Vertretung für ${k.trainer}` : 'Vertretung'}>{k.vertretung} <span className="text-xs text-muted-foreground">(Vertretung)</span></span>
              : (k.trainer || <span className="text-muted-foreground">–</span>))}
          {!bearbeitet && k.assistenz && <div className="text-xs text-muted-foreground">Assistenz: {k.assistenz}</div>}
        </td>
      )}
      <td className="px-2 py-2">
        <div className="flex items-center gap-2 min-w-[110px]" title={`${k.mitglieder} Mitglieder + ${k.probetrainings} Probetrainings`}>
          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.round((k.gebucht / (k.max || 1)) * 100))}%`, backgroundColor: k.gebucht >= k.max ? '#EF4444' : P1_ROT, opacity: k.gebucht >= k.max ? 1 : 0.75 }} /></div>
          {bearbeitet
            ? <span className="tabular-nums text-xs flex items-center gap-1">{k.gebucht}/<Input type="number" min={1} value={max} onChange={e => setMax(e.target.value)} className="h-7 text-xs w-14 px-1" onKeyDown={e => { if (e.key === 'Enter') speichern(); if (e.key === 'Escape') abbrechen(); }} /></span>
            : <span className="tabular-nums text-xs">{k.gebucht}/{k.max}</span>}
        </div>
      </td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-1">
          {k.badges.filter(b => b !== '2. Einheit verkaufbar' || k.mitglieder > 0).map(b => <Badge key={b} variant="outline" className={`text-[10px] whitespace-nowrap ${b === 'voll' ? 'border-red-300 text-red-600' : 'border-emerald-300 text-emerald-600'}`}>{b}</Badge>)}
          {darfBearbeiten && !bearbeitet && <button className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent" title="Trainer / Maximum ändern" onClick={() => setBearbeitet(true)} data-testid={`kurs-edit-${k.id}`}><Pencil className="w-3.5 h-3.5" /></button>}
          {bearbeitet && (
            <>
              <button className="p-1 rounded text-emerald-600 hover:bg-emerald-50" title="Speichern" onClick={speichern} disabled={speichert}>{speichert ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}</button>
              <button className="p-1 rounded text-muted-foreground hover:bg-accent" title="Abbrechen" onClick={abbrechen}><X className="w-3.5 h-3.5" /></button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

const AMPEL_CLS = { gruen: 'bg-emerald-500', orange: 'bg-amber-500', rot: 'bg-red-500', grau: 'bg-muted-foreground/30' };
const AMPEL_LABEL = { tag0: 'Tag 0', training1: '1. Training', tag2: 'Tag 2', checkup_gebucht: 'Check-Up', tag14: 'Tag 14' };

function BlockKopf({ titel, icon: Icon, rechts }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-2">
      <h2 className="text-sm font-bold flex items-center gap-1.5">{Icon && <Icon className="w-4 h-4" style={{ color: AKZENT }} />} {titel}</h2>
      {rechts}
    </div>
  );
}

export default function HeutePage() {
  const { user, isAdminPermissions, userPermissions } = useAuth();
  const navigate = useNavigate();
  const heute = useMemo(() => new Date(), []);
  const [kpi, setKpi] = useState(null);
  const [aufgaben, setAufgaben] = useState(null);
  const [kurse, setKurse] = useState(null);
  const [board, setBoard] = useState(null);
  const [laden, setLaden] = useState(true);
  const [alle, setAlle] = useState(false);
  // Zwei Ansichten (Betreiber 12.10.2026): der Inhaber landet auf der Übersicht, das Team arbeitet in der Arbeitsliste.
  const istInhaber = user?.role === 'admin';
  const [ansicht, setAnsicht] = useState(() => { try { return localStorage.getItem('p1_start_ansicht') || 'uebersicht'; } catch { return 'uebersicht'; } });
  const uebersicht = istInhaber && ansicht === 'uebersicht';
  // Arbeitsliste (Betreiber 12.10.2026: „Caglas Dashboard auch aufräumen"): oben Stempeluhr und nächster Termin, dann vier
  // eigene Zahlen und die Aufgaben; alles Übrige liegt zugeklappt darunter.
  const [termine, setTermine] = useState(null);
  const [auf, setAuf] = useState(null);
  const klappe = (name) => ({ offen: auf === name, onToggle: () => setAuf(auf === name ? null : name) });
  // Arbeitsbereiche (Betreiber 12.10.2026: „anklickbar, dass man drin arbeiten kann, und eine Liste, was gearbeitet werden muss"):
  // Ein Klick auf einen Bereich zeigt nur dessen Aufgaben, vollständig und aufgeklappt, mit einer Liste zum Kopieren.
  const [bereich, setBereich] = useState(null);
  const bereiche = (aufgaben?.gruppen || []).filter(g => g.quelle !== 'uebergabe').map(g => ({ ...g, art: g.sammel ? 'Rückgewinnung' : g.ablage ? 'Ablage' : 'Heute' }));
  const offenerBereich = bereiche.find(g => g.quelle === bereich) || null;
  const listeKopieren = async (g) => {
    const text = g.eintraege.map((e, i) => `${i + 1}. ${e.bezug?.name || 'ohne Namen'} — ${e.titel}${e.faellig_am ? ` (fällig ${new Date(e.faellig_am).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })})` : ''}`).join('\n');
    try { await navigator.clipboard.writeText(`${g.art}: ${g.label} (${g.anzahl})\n${text}`); toast.success(`Liste mit ${g.anzahl} Einträgen kopiert`); }
    catch { toast.error('Kopieren nicht möglich. Die Liste steht unten zum Markieren.'); }
  };
  // Übergabe (Betreiber 12.10.2026): die Leitung übergibt gesprochen oder getippt; bei der Empfängerin steht sie ganz oben
  const eigeneAufgaben = (aufgaben?.gruppen || []).filter(g => g.quelle !== 'uebergabe')
    .flatMap(g => g.eintraege.filter(e => e.owner === user?.user_id).map(e => ({ id: e.id, titel: e.titel, gruppe: `${g.sammel ? 'Rückgewinnung · ' : g.ablage ? 'Ablage · ' : ''}${g.label}` }))).slice(0, 200);
  const [ablageFrage, setAblageFrage] = useState(null);
  const ablegen = async (ab) => {
    setAblageFrage(null);
    try { await api.put('/aufgaben/ablage', { ablegen: ab }); toast.success(ab ? 'Abgelegt. Die Tagesliste ist frei.' : 'Ablage aufgehoben'); lade(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Nicht geändert'); }
  };
  const WICHTIG = ['leads_woche', 'probetrainings', 'abschluesse', 'abschlussquote'];
  const waehle = (a) => { setAnsicht(a); try { localStorage.setItem('p1_start_ansicht', a); } catch { /* gilt bis zum Neuladen */ } };
  const [nutzer, setNutzer] = useState([]);
  const istLeitung = user?.role === 'admin' || user?.role === 'manager' || isAdminPermissions;
  useEffect(() => { if (istLeitung) authAPI.users().then(r => setNutzer((r.data || []).filter(u => u.name))).catch(() => setNutzer([])); }, [istLeitung]);
  const darfKurse = istLeitung || !!(userPermissions || {})['kursplan.edit'];

  const lade = useCallback(async () => {
    setLaden(true);
    const [k, a, c, b] = await Promise.allSettled([kpiAPI.heute(), aufgabenAPI.heute(alle), kpiAPI.kursplanHeute(), onboardingAPI.board(30)]);
    if (k.status === 'fulfilled') setKpi(k.value.data); else toast.error('Kennzahlen konnten nicht geladen werden');
    if (a.status === 'fulfilled') setAufgaben(a.value.data); else toast.error('Aufgaben konnten nicht geladen werden');
    if (c.status === 'fulfilled') setKurse(c.value.data);
    if (b.status === 'fulfilled') setBoard(b.value.data);
    setLaden(false);
  }, [alle]);
  useEffect(() => { lade(); }, [lade]);
  useEffect(() => { if (!uebersicht) api.get('/startseite', { params: { nur: 'probetrainings,betrieb' } }).then(r => setTermine(r.data)).catch(() => setTermine(false)); }, [uebersicht]);

  const erledigen = async (a) => {
    try {
      await aufgabenAPI.erledigt(a.id);
      toast.success('Erledigt');
      setAufgaben((alt) => alt ? { ...alt, gruppen: alt.gruppen.map(g => ({ ...g, eintraege: g.eintraege.filter(e => e.id !== a.id), anzahl: g.anzahl - (g.eintraege.some(e => e.id === a.id) ? 1 : 0) })).filter(g => g.eintraege.length) } : alt);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erledigen fehlgeschlagen'); }
  };

  const entfernen = (ids) => setAufgaben((alt) => alt ? { ...alt, gruppen: alt.gruppen.map(g => ({ ...g, eintraege: g.eintraege.filter(e => !ids.includes(e.id)), anzahl: g.eintraege.filter(e => !ids.includes(e.id)).length })).filter(g => g.eintraege.length) } : alt);
  const verwerfen = async (a) => {
    if (!window.confirm(`„${a.titel}" verwerfen?`)) return;
    try { await aufgabenAPI.verwerfen(a.id, 'verworfen auf /heute'); entfernen([a.id]); toast.success('Verworfen'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Verwerfen fehlgeschlagen'); }
  };
  const verwerfenAlle = async (g) => {
    if (!window.confirm(`Alle ${g.anzahl} Einträge in „${g.label}" verwerfen? Das lässt sich nicht rückgängig machen.`)) return;
    try { const r = await aufgabenAPI.verwerfenMehrere(g.eintraege.map(e => e.id), `Gruppe ${g.label} verworfen`); toast.success(`${r.data.verworfen} verworfen`); lade(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Verwerfen fehlgeschlagen'); }
  };
  const delegieren = async (ids, uid) => {
    try {
      const r = await aufgabenAPI.delegieren(ids, uid);
      toast.success(uid ? `${r.data.delegiert} delegiert an ${r.data.an?.name || ''}` : 'Delegation aufgehoben');
      if (!alle && uid && uid !== user?.user_id) entfernen(ids); else lade();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delegieren fehlgeschlagen'); }
  };

  const datum = heute.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6" data-testid="heute-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{gruss(heute)}, {user?.name?.split(' ')[0] || 'Team'}.</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{datum}{aufgaben ? ` · ${aufgaben.gesamt} Aufgaben bis morgen${aufgaben.ueberfaellig ? `, ${aufgaben.ueberfaellig} überfällig` : ''}${aufgaben.altlast ? ` · ${aufgaben.altlast} Altlast` : ''}` : ''}</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {istInhaber && (
            <div className="inline-flex rounded-full border border-border bg-card p-0.5 text-xs font-semibold" role="group" aria-label="Ansicht" data-testid="heute-ansicht">
              {[['uebersicht', 'Übersicht'], ['arbeit', 'Arbeitsliste']].map(([k, l]) => (
                <button key={k} onClick={() => waehle(k)} aria-pressed={ansicht === k}
                  className={`rounded-full px-3 py-1.5 transition-colors ${ansicht === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>{l}</button>
              ))}
            </div>
          )}
          {!uebersicht && istLeitung && <Button size="sm" variant="outline" onClick={() => setAlle(a => !a)} data-testid="heute-alle">{alle ? 'Nur meine Aufgaben' : 'Aufgaben aller anzeigen'}</Button>}
          <Button size="sm" variant="outline" onClick={lade} disabled={laden} className="gap-1.5" data-testid="heute-refresh">
            {laden ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Aktualisieren
          </Button>
        </div>
      </div>

      {/* v8 M19: Morgenbriefing, Jetzt dran, Top 3, Übung des Tages — in beiden Ansichten ganz oben */}
      <HeuteCockpit onGeaendert={lade} />
      <PiaPanel />

      {/* Übergabe-Feld: in der Arbeitsliste ganz oben (dort wird abgearbeitet), in der Übersicht des Inhabers unter den
          Zahlen beim Team — Dashboard-Regel „Kennzahlen zuerst" */}
      {!uebersicht && <UebergabeFeld nutzer={nutzer} ich={user?.user_id} eigene={eigeneAufgaben} onGeaendert={lade} />}

      {uebersicht ? <StartUebersicht vorTeam={<UebergabeFeld nutzer={nutzer} ich={user?.user_id} eigene={eigeneAufgaben} onGeaendert={lade} />} /> : (<>
      <div className={`grid gap-4 ${termine?.probetrainings ? 'lg:grid-cols-2' : ''}`}>
        <Zeiterfassung />
        {termine?.probetrainings && (() => {
          const pt = termine.probetrainings, erster = pt.naechste[0];
          return (
            <Card data-testid="arbeit-termine">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="text-sm font-bold">Als Nächstes</h2>
                  <button className="text-xs font-medium text-primary hover:underline" onClick={() => navigate('/probetrainings')}>Alle Probetrainings ({pt.kommend})</button>
                </div>
                {!erster ? <p className="mt-1 text-sm text-muted-foreground">Kein Probetraining gebucht.{pt.ohne_termin ? ` ${pt.ohne_termin} Leads warten noch auf einen Termin.` : ''}</p> : (
                  <ul className="mt-1">
                    {pt.naechste.slice(0, 3).map((p, i) => (
                      <li key={p.lead_id}>
                        <button onClick={() => navigate(`/leads/${p.lead_id}`)} className="flex w-full items-center gap-3 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-accent">
                          <span className={`flex h-8 w-8 flex-none items-center justify-center rounded-full text-[11px] font-bold ${i === 0 ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'}`}>{kuerzel(p.name)}</span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold">{p.name}</span>
                            <span className="block truncate text-xs text-muted-foreground">{wann(p.tag, termine.heute)}{p.uhrzeit ? ` ${p.uhrzeit}` : ''}{p.kurs ? ` · ${p.kurs}` : ''}</span>
                          </span>
                          {!p.im_kurs_gebucht && !p.mitglied && <span className="flex-none rounded-full border border-amber-600/50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400">ohne Kursplatz</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          );
        })()}
      </div>

      {/* Block 1 */}
      <section>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {(kpi?.kacheln || []).filter(k => WICHTIG.includes(k.key)).map(k => <Kachel key={k.key} k={k} navigate={navigate} />)}
        </div>
        {kpi && kpi.vorwoche_datum == null && <p className="text-xs text-muted-foreground mt-2">Der erste Nacht-Snapshot läuft heute Nacht — ab nächster Woche erscheint der Vergleich zur Vorwoche.</p>}
      </section>

      {bereiche.length > 0 && (
        <nav className="flex flex-wrap items-center gap-2" aria-label="Arbeitsbereiche" data-testid="arbeit-bereiche">
          <span className="text-xs font-semibold text-muted-foreground mr-1">Arbeitsbereiche</span>
          {bereiche.map(g => (
            <button key={g.quelle} onClick={() => setBereich(bereich === g.quelle ? null : g.quelle)} aria-pressed={bereich === g.quelle}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${bereich === g.quelle ? 'border-primary bg-primary text-primary-foreground font-semibold' : 'border-border bg-card hover:bg-accent'}`}>
              {g.art !== 'Heute' && <span className="opacity-70">{g.art} · </span>}{g.label} <span className="tabular-nums font-semibold">{g.anzahl}</span>
            </button>
          ))}
        </nav>
      )}

      {offenerBereich ? (
        <section className="space-y-3" data-testid="arbeit-fokus">
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="outline" onClick={() => setBereich(null)}>Zurück zur Arbeitsliste</Button>
            <h2 className="text-base font-bold">{offenerBereich.art !== 'Heute' ? `${offenerBereich.art}: ` : ''}{offenerBereich.label}</h2>
            <span className="text-sm text-muted-foreground">{offenerBereich.anzahl} {offenerBereich.anzahl === 1 ? 'Aufgabe' : 'Aufgaben'}</span>
            <Button size="sm" variant="outline" className="ml-auto" onClick={() => listeKopieren(offenerBereich)} data-testid="arbeit-kopieren">Liste kopieren</Button>
          </div>
          <AufgabenGruppe key={`fokus-${offenerBereich.quelle}`} immerOffen g={offenerBereich} heute={heute} onErledigt={erledigen} onVerwerfen={verwerfen} onDelegieren={delegieren} onVerwerfenAlle={verwerfenAlle} navigate={navigate} leitung={istLeitung} nutzer={nutzer} zeigeOwner={alle} />
          <details className="rounded-xl border border-border bg-card p-3 text-xs">
            <summary className="cursor-pointer font-semibold">Als einfache Liste zum Abhaken oder Ausdrucken</summary>
            <ol className="mt-2 list-decimal space-y-1 pl-5" data-testid="arbeit-liste">
              {offenerBereich.eintraege.map(e => (
                <li key={e.id}><b>{e.bezug?.name || 'ohne Namen'}</b> — {e.titel}{e.faellig_am ? ` · fällig ${new Date(e.faellig_am).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}` : ''}</li>
              ))}
            </ol>
          </details>
        </section>
      ) : (
      <div className="space-y-4">
        {/* Block 2 */}
        <section id="aufgaben" className="scroll-mt-4">
          <BlockKopf titel="Aufgaben heute" icon={ClipboardList} rechts={aufgaben?.ueberfaellig ? <Badge variant="destructive">{aufgaben.ueberfaellig} überfällig</Badge> : null} />
          <div className="space-y-3">
            {aufgaben && aufgaben.gruppen.length === 0 && <Card className="border-border/60"><CardContent className="p-4 text-sm text-muted-foreground">Nichts offen bis morgen. 🎉</CardContent></Card>}
            {(aufgaben?.gruppen || []).filter(g => !g.sammel && !g.ablage && g.quelle !== 'uebergabe').map(g => <AufgabenGruppe key={g.quelle} g={g} heute={heute} onErledigt={erledigen} onVerwerfen={verwerfen} onDelegieren={delegieren} onVerwerfenAlle={verwerfenAlle} navigate={navigate} leitung={istLeitung} nutzer={nutzer} zeigeOwner={alle} />)}
          </div>
        </section>

        <Card data-testid="arbeit-mehr"><CardContent className="p-0 [&_.p1-karte]:border-0 [&_.p1-karte]:shadow-none">
          {/* Sammelbereich (Betreiber 12.10.2026): Kündigungen und alte Leads bleiben offen, verstopfen aber die Tagesliste nicht */}
          {(aufgaben?.gruppen || []).some(g => g.sammel) && (
            <Klappe titel="Rückgewinnung" {...klappe('sammel')}
              zaehler={[aufgaben.sammel?.kuendigung && `${aufgaben.sammel.kuendigung} Kündigungen`, aufgaben.sammel?.alte_leads && `${aufgaben.sammel.alte_leads} alte Leads`].filter(Boolean).join(' · ')}>
              <p className="mb-3 text-xs text-muted-foreground">Ohne Frist: Diese Aufgaben zählen nicht als überfällig. Arbeite sie ab, wenn die Tagesliste leer ist, die ältesten stehen oben.</p>
              <div className="space-y-3" data-testid="arbeit-sammel">
                {(aufgaben?.gruppen || []).filter(g => g.sammel).map(g => <AufgabenGruppe key={g.quelle} g={g} heute={heute} onErledigt={erledigen} onVerwerfen={verwerfen} onDelegieren={delegieren} onVerwerfenAlle={verwerfenAlle} navigate={navigate} leitung={istLeitung} nutzer={nutzer} zeigeOwner={alle} />)}
              </div>
            </Klappe>
          )}
          {/* Ablage „ad acta" (Betreiber 12.10.2026): alles vor dem Stichtag Fällige — offen und einsehbar, aber nirgends gezählt */}
          {(aufgaben?.ablage?.anzahl > 0 || istLeitung) && (
            <Klappe titel="Ablage" {...klappe('ablage')}
              zaehler={aufgaben?.ablage?.stichtag ? `${aufgaben.ablage.anzahl} alte Aufgaben, abgelegt am ${new Date(aufgaben.ablage.stichtag).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}` : 'nichts abgelegt'}>
              <p className="mb-3 text-xs text-muted-foreground">
                {aufgaben?.ablage?.stichtag
                  ? `Alles, was vor dem ${new Date(aufgaben.ablage.stichtag).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} Uhr fällig war. Die Aufgaben sind weiter offen und lassen sich hier erledigen, zählen aber nicht mehr als fällig oder überfällig.`
                  : 'Mit einem Klick legst du alles ab, was bis jetzt fällig war. Die Aufgaben bleiben offen und hier einsehbar, zählen aber nicht mehr.'}
              </p>
              {istLeitung && (
                <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="ablage-knoepfe">
                  {ablageFrage ? (<>
                    <span className="text-xs">{ablageFrage === 'ab' ? `Wirklich alle ${aufgaben?.ueberfaellig ?? ''} überfälligen und heute fälligen Aufgaben des ganzen Teams ablegen?` : 'Ablage aufheben? Alle abgelegten Aufgaben stehen dann wieder in der Tagesliste.'}</span>
                    <Button size="sm" onClick={() => ablegen(ablageFrage === 'ab')}>Ja</Button>
                    <Button size="sm" variant="outline" onClick={() => setAblageFrage(null)}>Abbrechen</Button>
                  </>) : (<>
                    <Button size="sm" variant="outline" onClick={() => setAblageFrage('ab')} data-testid="ablage-setzen">Alles bis jetzt ad acta legen</Button>
                    {aufgaben?.ablage?.stichtag && <Button size="sm" variant="outline" onClick={() => setAblageFrage('auf')} data-testid="ablage-aufheben">Ablage aufheben</Button>}
                  </>)}
                </div>
              )}
              <div className="space-y-3" data-testid="arbeit-ablage">
                {(aufgaben?.gruppen || []).filter(g => g.ablage).map(g => <AufgabenGruppe key={g.quelle} g={g} heute={heute} onErledigt={erledigen} onVerwerfen={verwerfen} onDelegieren={delegieren} onVerwerfenAlle={verwerfenAlle} navigate={navigate} leitung={istLeitung} nutzer={nutzer} zeigeOwner={alle} />)}
              </div>
            </Klappe>
          )}
          {/* Block 3 */}
          <Klappe titel="Kurse heute" zaehler={kurse ? `${kurse.kurse.length} Kurse` : ''} {...klappe('kurse')}>
          <section>
            <BlockKopf titel="Kurse heute" icon={CalendarRange} rechts={<span className="flex items-center gap-2">{darfKurse && <button className="text-xs hover:underline" style={{ color: AKZENT }} onClick={() => navigate('/kursplan')}>Kursplan öffnen</button>}{kurse && <Badge variant="secondary">{kurse.tag}</Badge>}</span>} />
            <Card className="border-border/60">
              <CardContent className="p-0 overflow-x-auto">
                {kurse && kurse.kurse.length === 0 && <p className="p-4 text-sm text-muted-foreground">Heute keine Kurse.</p>}
                {kurse && kurse.kurse.length > 0 && (
                  <table className="w-full text-sm">
                    <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                      <th className="text-left font-medium px-4 py-2">Uhrzeit</th><th className="text-left font-medium px-2 py-2">Kurs</th>{kurse.kurse.some(k => k.trainer) && <th className="text-left font-medium px-2 py-2">Trainer</th>}<th className="text-left font-medium px-2 py-2">Belegung</th><th className="px-2 py-2"></th></tr></thead>
                    <tbody>
                      {kurse.kurse.map(k => <KursZeile key={k.id} k={k} darfBearbeiten={darfKurse} onGespeichert={lade} zeigeTrainer={kurse.kurse.some(x => x.trainer)} />)}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </section>
          </Klappe>

          {/* Block 4 */}
          <Klappe titel="Neumitglieder 30 Tage" zaehler={board ? `${board.anzahl} neu` : ''} {...klappe('neu')}>
          <section>
            <BlockKopf titel="Neumitglieder 30 Tage" icon={Sparkles} rechts={board && <Badge variant="secondary">{board.anzahl}</Badge>} />
            <Card className="border-border/60">
              <CardContent className="p-4">
                {board && board.eintraege.length === 0 && <p className="text-sm text-muted-foreground">Keine neuen Mitglieder in den letzten 30 Tagen.</p>}
                <div>
                  {(board?.eintraege || []).map(m => (
                    <div key={m.lead_id} className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 items-center py-2.5 border-t border-border/50 first:border-t-0 cursor-pointer hover:bg-accent/40 -mx-2 px-2 rounded-md" onClick={() => navigate(`/leads/${m.lead_id}`)} data-testid={`onboarding-${m.lead_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">{m.name}
                          {m.warnung && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 gap-1"><AlertTriangle className="w-3 h-3" />{m.warnung}</Badge>}
                          {m.no_show && <Badge variant="destructive" className="text-[10px]">No-Show</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground">Tag {m.tage_dabei} · {m.kurs || 'Kurs offen'}{m.trainer ? ` · ${m.trainer}` : ''} · {m.trainings} Training{m.trainings === 1 ? '' : 's'}</div>
                      </div>
                      <div className="flex gap-1.5">
                        {Object.entries(m.ampel).map(([s, p]) => (
                          <span key={s} className={`w-3.5 h-3.5 rounded-full ring-1 ring-border ${AMPEL_CLS[p.farbe]}`} title={`${AMPEL_LABEL[s]}: ${p.farbe === 'gruen' ? 'erledigt' : p.farbe === 'rot' ? 'überfällig' : p.farbe === 'orange' ? 'heute fällig' : 'offen'}${p.faellig_am ? ' · ' + tagUhr(p.faellig_am, heute) : ''}`} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {board && board.eintraege.length > 0 && <p className="text-[10px] text-muted-foreground mt-2">Punkte: Tag 0 · 1. Training · Tag 2 · Check-Up · Tag 14 — grün erledigt, gelb heute, rot überfällig.</p>}
              </CardContent>
            </Card>
          </section>
          </Klappe>
          <Klappe titel="Starterpakete" zaehler={termine?.betrieb ? (termine.betrieb.starterpakete_offen ? `${termine.betrieb.starterpakete_offen} offen` : 'alle ausgegeben') : ''} {...klappe('starter')}>
            <StarterpaketKarte />
          </Klappe>
          <Klappe titel="Weitere Zahlen" zaehler="Bestand, Kündigungen, Follow-ups, Upsells" {...klappe('zahlen')}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {(kpi?.kacheln || []).filter(k => !WICHTIG.includes(k.key)).map(k => <Kachel key={k.key} k={k} navigate={navigate} />)}
            </div>
          </Klappe>
          {istLeitung && (
            <Klappe titel="Zuständigkeiten" zaehler="wer bekommt welche Aufgaben" {...klappe('zust')}>
              <Zustaendigkeiten nutzer={nutzer} />
            </Klappe>
          )}
        </CardContent></Card>
      </div>
      )}
      </>)}
    </div>
  );
}
